import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getNavigation, getAppToken, recordPageView } from '../api/client.js';
import { useSession } from '../context/SessionContext.jsx';
import {
  framedUrl, replyToFrameMessage, themeMessage
} from '../lib/embedTheme.js';

/**
 * Hosts an embedded third-party site.
 *
 * Three changes from the old implementation:
 *
 * 1. The URL is not taken from the DOM. The old dashClient read a data-id
 *    attribute off the clicked <a> and dropped it straight into iframe src,
 *    so anything that could write to the DOM could frame anything. Here the
 *    route carries a profile item id and the URL comes from the API, which
 *    has already checked the item belongs to the user's profile.
 *
 * 2. The iframe is sandboxed. Embedded partner apps get scripts, forms and
 *    same-origin, but not top-navigation, popups or downloads.
 *
 * 3. The theme is handed across the boundary — see lib/embedTheme.js. Nothing
 *    cascades into an iframe, so before this an embedded app had no way to
 *    know the portal was in dark mode.
 */
export default function WebsiteView() {
  const { itemId } = useParams();
  const { theme } = useSession();
  const [frame, setFrame] = useState(null);      // { src, origin }
  const [error, setError] = useState(null);
  const iframeRef = useRef(null);

  /* The current theme, in a ref.
     Assigned during render, so any effect reading it after that render sees
     the live value — but reading a ref does not subscribe, which is the whole
     point: see the dependency array below. */
  const themeRef = useRef(theme);
  themeRef.current = theme;

  const { data: nav } = useQuery({
    queryKey: ['navigation', 'Desktop'],
    queryFn: () => getNavigation('Desktop')
  });

  const item = nav?.groups
    .flatMap(g => g.items)
    .find(i => String(i.id) === String(itemId));

  /* Building the frame.
   *
   * The dependency array is `[item]` and must stay that way. Reading `theme`
   * here and adding it to the deps looks like the obvious way to keep the URL
   * current, and it is a trap: every toggle would re-run this, change `src`,
   * RELOAD the frame — discarding whatever state the app held — and call
   * getAppToken again, burning a five-minute credential per click of the
   * theme button.
   *
   * So the query parameter is set once, from the theme at the moment the frame
   * is built, and every subsequent change travels by postMessage in the effect
   * below. Reading themeRef rather than `theme` is what keeps it out of the
   * deps — and reading it here rather than at first render also means a theme
   * that arrives late from /api/me still reaches the URL.
   */
  useEffect(() => {
    let cancelled = false;
    setFrame(null);
    setError(null);
    if (!item) return;

    (async () => {
      try {
        if (item.requiresAppToken) {
          // Short-lived, audience-bound token minted server side.
          const { url, token } = await getAppToken(item.id);
          if (cancelled) return;
          setFrame(framedUrl({ targetUrl: url, token, theme: themeRef.current }));
        } else {
          /* The no-token branch needs the theme too. It used to set src
             straight from item.targetUrl with no URL construction at all,
             which is exactly how a change like this gets made in one branch
             and forgotten in the other. */
          setFrame(framedUrl({ targetUrl: item.targetUrl, theme: themeRef.current }));
          // Fire and forget; telemetry must not block the view.
          recordPageView(item.id).catch(() => {});
        }
      } catch (err) {
        if (!cancelled) setError(err);
      }
    })();

    return () => { cancelled = true; };
  }, [item]);

  /* Telling the app about a change.
   *
   * Skipped on the first run: the URL already carries that value, and posting
   * it again before the app has loaded its listener achieves nothing. After
   * that, every toggle posts — at the frame's own origin, never '*', because
   * this message says something about the user's session and only the app we
   * framed has any business reading it.
   */
  const posted = useRef(false);
  useEffect(() => {
    if (!frame) { posted.current = false; return; }
    if (!posted.current) { posted.current = true; return; }

    iframeRef.current?.contentWindow?.postMessage(themeMessage(theme), frame.origin);
  }, [theme, frame]);

  /* Answering an app that announces itself.
   *
   * A module script is deferred by specification, so a partner app's listener
   * may not exist until after we minted its URL — and the theme may have
   * changed in between. `vizora:ready` closes that gap, and is also how an app
   * framed by an older portal build still ends up themed.
   *
   * Keyed on the origin alone, not on the theme: re-subscribing on every
   * toggle would drop a message arriving mid-swap. The current value is read
   * from themeRef at the moment we answer.
   */
  useEffect(() => {
    if (!frame) return;

    function onMessage(event) {
      const reply = replyToFrameMessage(event, {
        frameOrigin: frame.origin,
        frameWindow: iframeRef.current?.contentWindow,
        theme: themeRef.current
      });
      if (reply) event.source?.postMessage(themeMessage(reply), frame.origin);
    }

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [frame]);

  if (!nav) return <div className="embed-overlay"><div className="spinner-border text-primary" /></div>;

  if (!item) {
    return <div className="admin-page">
      <div className="alert alert-warning">That item is no longer available on your profile.</div>
    </div>;
  }

  if (error) {
    return <div className="admin-page">
      <div className="alert alert-danger">Could not open {item.label}: {error.message}</div>
    </div>;
  }

  return (
    <div className="embed-wrapper">
      {!frame && <div className="embed-overlay"><div className="spinner-border text-primary" /></div>}
      {frame && (
        <iframe
          ref={iframeRef}
          title={item.label}
          src={frame.src}
          className="embed-container"
          sandbox="allow-scripts allow-forms allow-same-origin"
          referrerPolicy="strict-origin"
          allowFullScreen
        />
      )}
    </div>
  );
}
